import { AccessorKeyColumnDef } from "@tanstack/react-table";
import { Tooltip } from "antd";
import i18next from "i18next";
import { ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IArrhythmiaStatus,
  IPatientMeasurementData,
} from "../../../../../../@types/Measurements";
import { IStaffPreferences } from "../../../../../../@types/Preferences";
import { Dots } from "../../../../../../assets/icons/Dots";
import { ReactComponent as NoMessagesImage } from "../../../../../../assets/images/woman-computer.svg";
import { CustomIconButton } from "../../../../../../components/CustomIconButton";
import Dropdown, {
  DropdownTextItem,
} from "../../../../../../components/Dropdown/Dropdown";
import { createColumn, Table } from "../../../../../../components/Table";
import { ActionContainer } from "../../../../../../components/TableActionDropdownButton/TableActionDropdownButton.styles";
import { Tag } from "../../../../../../components/Tag";
import { AUTH_STAFF_PREFERENCES } from "../../../../../../constants/localStorageKeys";
import {
  fibriCheckColorDictionary,
  archivableSpirometryMeasurements,
  tooltipTitles,
} from "../../../../../../constants/measurements";
import { getFriendlySourceName } from "../../../../../../utils/chartSourceUtils";
import { getArrhythmiaStateLabel } from "../../../../../../utils/measurementsUtils";
import { safeStorage, safeJsonParse } from "../../../../../../utils/storage";
import { useArchiveMeasurement } from "../../hooks/useArchiveMeasurement";
import { ArchiveSpirometryConfirmationModal } from "../ArchiveSpirometryModal";
import {
  ImageContainer,
  NoContentMessage,
  TableContainer,
} from "./ModalMeasurementsTable.styles";

interface IModalMeasurements {
  data: Map<string, any[]>;
  measurementType: string;
  patientId?: string;
  isPinnedGraph?: boolean;
  unit?: string;
}

interface IMeasuremntTableData {
  id: number;
  measurement: string;
  source: string;
  time: string;
  unformattedTime: number;
  action: ReactElement;
  archivedBy?: string;
  arrhythmia_status?: ReactElement;
  bkv_status?: ReactElement;
  bkv_value?: string;
  oxygen_flow_rate?: string;
}

/**
 * The dropdown emits an item's `value`, falling back to its `text` — so a translated item with
 * no `value` would emit the translated label and stop matching here (EN-5191).
 */
const ARCHIVE_VALUE = "archiveValue";
const UNARCHIVE_VALUE = "unarchiveValue";

export function ModalMeasurementsTable({
  data,
  measurementType,
  patientId,
  isPinnedGraph,
  unit,
}: IModalMeasurements) {
  const { t } = useTranslation();

  const { archiveMeasurement } = useArchiveMeasurement();

  if (data.size < 1) {
    return (
      <ImageContainer>
        <NoMessagesImage width={181} height={158} />
        <NoContentMessage>
          {measurementType === "arrhythmia_status" || measurementType === "bkv"
            ? t("patientPage_measurementModal_table_emptyRange")
            : t("patientPage_measurementModal_table_emptyFilters")}
        </NoContentMessage>
      </ImageContainer>
    );
  }

  const mapKeys = Array.from(data.keys());
  let header = tooltipTitles.get(mapKeys[0].split("|")[0]) ?? "";

  const [tableData] = useState<IMeasuremntTableData[]>([]);
  const [modalMeasurementsTableColumns, setModalMeasurementsTableColumns] =
    useState<AccessorKeyColumnDef<any, string>[]>([]);
  const [confirmModalData, setConfirmModalData] =
    useState<IPatientMeasurementData>();
  const [archiveMeasurementValue, setArchivedMeasurementValue] =
    useState<number>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const staffPreferences = safeStorage.getItem(AUTH_STAFF_PREFERENCES);
    const parsedStaffPreferences = safeJsonParse<IStaffPreferences>(
      staffPreferences,
      {} as IStaffPreferences
    );

    const tempModalMeasurementsTableColumns = [
      ...(measurementType !== "bkv"
        ? [
            createColumn({
              keyName: "measurement",
              header: () => header,
              cell: (info) => info.renderValue(),
              size: 100,
            }),
          ]
        : []),
      ...(measurementType === "pulse_ox"
        ? [
            createColumn({
              keyName: "oxygen_flow_rate",
              header: () => i18next.t("patientPage_columns_oxygen_flow_rate"),
              cell: (info) => info.renderValue(),
              size: 130,
            }),
          ]
        : []),
      createColumn({
        keyName: "source",
        header: () => i18next.t("patientPage_columns_source"),
        cell: (info) => info.renderValue(),
        size: 100,
      }),
      createColumn({
        keyName: "time",
        header: () => i18next.t("patientPage_columns_date"),
        cell: (info) => info.renderValue(),
        size: 120,
      }),
    ];

    if (measurementType === "bkv") {
      tempModalMeasurementsTableColumns.push(
        createColumn({
          keyName: "bkv_status",
          header: () => i18next.t("patientPage_columns_status"),
          cell: (info) => info.renderValue(),
          size: 100,
        })
      );

      tempModalMeasurementsTableColumns.push(
        createColumn({
          keyName: "bkv_value",
          header: () => i18next.t("patientPage_columns_value"),
          cell: (info) => info.renderValue(),
          size: 100,
        })
      );
    }

    if (parsedStaffPreferences.archiveMeasurements) {
      const columnsWithAction = [
        ...tempModalMeasurementsTableColumns,
        createColumn({
          keyName: "action",
          header: () => "",
          cell: (info) => info.renderValue(),
          size: 1,
        }),
      ];

      setModalMeasurementsTableColumns(columnsWithAction);
    } else {
      setModalMeasurementsTableColumns(tempModalMeasurementsTableColumns);
    }
  }, []);

  const handleArrhythmiaTag = (measurement: any) => {
    const parsedObject = JSON.parse(measurement.arrhythmia_status);

    const arrhythmiaStatus: IArrhythmiaStatus = {
      key: parsedObject.Key,
      color: parsedObject.Color,
    };

    const formattedKey = getArrhythmiaStateLabel(arrhythmiaStatus.key, t);

    const tagColor = fibriCheckColorDictionary[arrhythmiaStatus.color] as
      | "success"
      | "info"
      | "tertiary"
      | "error"
      | "warning";

    return <Tag variant={tagColor}>{formattedKey}</Tag>;
  };

  const handleBkvTag = (measurement: any) => {
    const isNumber = !Number.isNaN(Number(measurement.bkv));
    const isNotDetected = measurement.bkv.toLowerCase() === "not detected";

    const tagColor = isNotDetected ? "success" : "error";
    const value = !isNumber ? measurement.bkv : " - ";

    return isNumber ? undefined : <Tag variant={tagColor}>{value}</Tag>;
  };

  const handleBkvValue = (measurement: any) => {
    const isNumber = !Number.isNaN(Number(measurement.bkv));

    const value = !isNumber ? "-" : `${measurement.bkv} ${unit}`;

    return value;
  };

  const formatOxygenFlowRate = (value: number | null | undefined) =>
    value === null || value === undefined ? "-" : String(value);

  const formatValue = (key: string, value: number) => {
    if (key === "lab_gfr" && value >= 90) {
      return ">90";
    }
    return value;
  };

  mapKeys.forEach((key) => {
    const measurementType = key.split("|")[0];

    if (measurementType === "arrhythmia_status" && header !== "BPM") {
      header = "BPM";

      modalMeasurementsTableColumns.splice(
        modalMeasurementsTableColumns.length - 1,
        0,
        createColumn({
          keyName: "arrhythmia_status",
          header: () => i18next.t("patientPage_columns_status"),
          cell: (info) => info.renderValue(),
          size: 200,
        })
      );
    }

    data.get(key)?.forEach((measurement) => {
      if (measurementType === "bp_systolic") {
        header = "Systolic/Diastolic";

        const DiastolicMeasurement = data
          .get(`bp_diastolic|${measurement.source.toLowerCase()}`)
          ?.find((element) => element.id === measurement.id);

        tableData.push({
          id: measurement.id,
          measurement: `${measurement[measurementType]}/${
            DiastolicMeasurement ? DiastolicMeasurement.bp_diastolic : "-"
          }`,
          source: measurement.source_name,
          time: measurement.time,
          unformattedTime: measurement.unformattedTime,
          archivedBy: measurement.archived_by,
          action: (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <ActionContainer className="actionContainer">
                <Dropdown
                  trigger={
                    <CustomIconButton>
                      <Dots />
                    </CustomIconButton>
                  }
                  value=""
                  onValueChange={(val: string) => {
                    if (val === ARCHIVE_VALUE && patientId !== undefined) {
                      archiveMeasurement(
                        measurement.id,
                        true,
                        measurementType,
                        patientId,
                        tooltipTitles.get(measurementType)
                      );
                    }
                    if (val === UNARCHIVE_VALUE && patientId !== undefined) {
                      archiveMeasurement(
                        measurement.id,
                        false,
                        measurementType,
                        patientId,
                        tooltipTitles.get(measurementType)
                      );
                    }
                  }}
                >
                  {measurement.archived_by === undefined ||
                  measurement.archived_by === "" ? (
                    <DropdownTextItem
                      key={ARCHIVE_VALUE}
                      value={ARCHIVE_VALUE}
                      text={t(
                        "patientPage_measurementModal_table_archiveValue"
                      )}
                    />
                  ) : (
                    <DropdownTextItem
                      key={UNARCHIVE_VALUE}
                      value={UNARCHIVE_VALUE}
                      text={t(
                        "patientPage_measurementModal_table_unarchiveValue"
                      )}
                    />
                  )}
                </Dropdown>
              </ActionContainer>
            </div>
          ),
        });

        return;
      }

      if (measurementType === "bp_diastolic") {
        return;
      }

      tableData.push({
        id: measurement.id,
        measurement:
          measurementType !== "arrhythmia_status"
            ? formatValue(measurementType, measurement[key.split("|")[0]])
            : measurement.heart_rate,
        source:
          measurementType === "daily_steps"
            ? getFriendlySourceName(measurement.source, t)
            : measurement.source_name,
        time: measurement.time,
        unformattedTime: measurement.unformattedTime,
        archivedBy: measurement.archived_by,
        arrhythmia_status:
          measurementType === "arrhythmia_status"
            ? handleArrhythmiaTag(measurement)
            : undefined,
        bkv_status:
          measurementType === "bkv" ? handleBkvTag(measurement) : undefined,
        bkv_value:
          measurementType === "bkv" ? handleBkvValue(measurement) : undefined,
        oxygen_flow_rate:
          measurementType === "pulse_ox"
            ? formatOxygenFlowRate(measurement.oxygen_rate)
            : undefined,
        action: (
          <div style={{ marginLeft: "50%" }}>
            <ActionContainer className="actionContainer">
              <Dropdown
                trigger={
                  <CustomIconButton>
                    <Dots />
                  </CustomIconButton>
                }
                value=""
                onValueChange={(val: string) => {
                  if (val === ARCHIVE_VALUE && patientId !== undefined) {
                    if (
                      archivableSpirometryMeasurements.includes(measurementType)
                    ) {
                      setArchivedMeasurementValue(measurement[measurementType]);
                      setConfirmModalData(measurement);
                    } else {
                      archiveMeasurement(
                        measurement.id,
                        true,
                        measurementType,
                        patientId,
                        tooltipTitles.get(measurementType)
                      );
                    }
                  }
                  if (val === UNARCHIVE_VALUE && patientId !== undefined) {
                    archiveMeasurement(
                      measurement.id,
                      false,
                      measurementType,
                      patientId,
                      tooltipTitles.get(measurementType)
                    );
                  }
                }}
              >
                {measurement.archived_by === undefined ||
                measurement.archived_by === "" ? (
                  <div>
                    <Tooltip
                      title={
                        measurement.source === "eMedRenal"
                          ? t(
                              "patientPage_measurementModal_table_emedCannotDelete"
                            )
                          : undefined
                      }
                      zIndex={3002}
                      color="#4B4B4B"
                      arrow={false}
                      styles={{
                        body: {
                          minHeight: "40px",
                          minWidth: "220px",
                          borderRadius: "4px !important",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                        },
                      }}
                    >
                      <div>
                        <DropdownTextItem
                          key={ARCHIVE_VALUE}
                          value={ARCHIVE_VALUE}
                          text={t(
                            "patientPage_measurementModal_table_archiveValue"
                          )}
                          disabled={measurement.source === "eMedRenal"}
                        />
                      </div>
                    </Tooltip>
                  </div>
                ) : (
                  <DropdownTextItem
                    key={UNARCHIVE_VALUE}
                    value={UNARCHIVE_VALUE}
                    text={t(
                      "patientPage_measurementModal_table_unarchiveValue"
                    )}
                  />
                )}
              </Dropdown>
            </ActionContainer>
          </div>
        ),
      });
    });
  });

  tableData.sort((a, b) => b.unformattedTime - a.unformattedTime);

  const handleConfirmModalClose = async (confirm: boolean) => {
    if (patientId === undefined) return;

    if (confirm && confirmModalData) {
      setIsLoading(true);
      await archiveMeasurement(
        confirmModalData.id,
        true,
        measurementType,
        patientId,
        tooltipTitles.get(measurementType)
      );
      setIsLoading(false);
    }

    setConfirmModalData(undefined);
  };

  return (
    <>
      <TableContainer isPinnedGraph={isPinnedGraph}>
        <Table
          fontWeight={400}
          hasBorder={false}
          data={tableData}
          columns={modalMeasurementsTableColumns}
        />
      </TableContainer>

      {confirmModalData !== undefined ? (
        <ArchiveSpirometryConfirmationModal
          archiveMeasurementValue={archiveMeasurementValue}
          isLoading={isLoading}
          unit={unit}
          time={confirmModalData.time}
          handleConfirmModalClose={handleConfirmModalClose}
        />
      ) : null}
    </>
  );
}
